import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Wifi, Loader2, Mail, Phone } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "French as a Foreign Language (FLE)",
  "French for Children",
  "Tutoring for Teens",
  "AP French Preparation",
  "TEF / TCF Preparation",
  "French Conversation",
  "Pronunciation Coaching",
  "Culture & Etiquette",
];
const LEVELS = ["Beginner", "Intermediate", "Advanced"];
const FORMAT_OPTS = ["Online", "In person (Katy / Houston)"];

const inputCls =
  "w-full bg-transparent border-b border-sand focus:border-gold outline-none py-3 text-base placeholder:text-navy/30 transition-colors";

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    service: SERVICES[0], level: LEVELS[0], format: FORMAT_OPTS[0], message: "",
  });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/enquiries`, form);
      setStatus("success");
      toast.success("Thank you! Your enquiry has been sent.");
      setForm({ name: "", email: "", phone: "", service: SERVICES[0], level: LEVELS[0], format: FORMAT_OPTS[0], message: "" });
    } catch (err) {
      setStatus("error");
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-36 bg-navy text-cream" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.35em] text-gold font-medium">First step</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-6 leading-tight">
              Let's begin <span className="italic text-gold">together.</span>
            </h2>
            <p className="mt-8 text-cream/70 leading-relaxed max-w-md">
              Tell me about your project: your level, your goals, your wishes.
              I personally reply to every enquiry to build your program.
            </p>
            <div className="mt-10 space-y-4 text-sm text-cream/70">
              <a href="mailto:cecilekinnunen@gmail.com" className="flex items-center gap-3 hover:text-cream transition-colors" data-testid="contact-email-link">
                <Mail size={16} className="text-gold shrink-0" /> cecilekinnunen@gmail.com
              </a>
              <a href="tel:+15619550794" className="flex items-center gap-3 hover:text-cream transition-colors" data-testid="contact-phone-link">
                <Phone size={16} className="text-gold shrink-0" /> (561) 955-0794
              </a>
              <p className="flex items-center gap-3"><MapPin size={16} className="text-gold shrink-0" /> Houston area — Katy, Cinco Ranch, Fulshear, Richmond</p>
              <p className="flex items-center gap-3"><Wifi size={16} className="text-gold shrink-0" /> Online lessons, worldwide</p>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-x-10 gap-y-8" data-testid="contact-form">
              <div>
                <label htmlFor="contact-name" className="text-xs uppercase tracking-[0.25em] text-cream/60">Name *</label>
                <input id="contact-name" required value={form.name} onChange={set("name")} placeholder="Your name"
                  className={`${inputCls} border-cream/25 focus:border-gold`} data-testid="contact-name-input" />
              </div>
              <div>
                <label htmlFor="contact-email" className="text-xs uppercase tracking-[0.25em] text-cream/60">Email *</label>
                <input id="contact-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com"
                  className={`${inputCls} border-cream/25 focus:border-gold`} data-testid="contact-email-input" />
              </div>
              <div>
                <label htmlFor="contact-phone" className="text-xs uppercase tracking-[0.25em] text-cream/60">Phone</label>
                <input id="contact-phone" value={form.phone} onChange={set("phone")} placeholder="( optional )"
                  className={`${inputCls} border-cream/25 focus:border-gold`} data-testid="contact-phone-input" />
              </div>
              <div>
                <label htmlFor="contact-service" className="text-xs uppercase tracking-[0.25em] text-cream/60">Lesson of interest</label>
                <select id="contact-service" value={form.service} onChange={set("service")}
                  className={`${inputCls} border-cream/25 focus:border-gold bg-navy`} data-testid="contact-service-select">
                  {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="contact-level" className="text-xs uppercase tracking-[0.25em] text-cream/60">Level</label>
                <select id="contact-level" value={form.level} onChange={set("level")}
                  className={`${inputCls} border-cream/25 focus:border-gold bg-navy`} data-testid="contact-level-select">
                  {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="contact-format" className="text-xs uppercase tracking-[0.25em] text-cream/60">Format</label>
                <select id="contact-format" value={form.format} onChange={set("format")}
                  className={`${inputCls} border-cream/25 focus:border-gold bg-navy`} data-testid="contact-format-select">
                  {FORMAT_OPTS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-message" className="text-xs uppercase tracking-[0.25em] text-cream/60">Your message</label>
                <textarea id="contact-message" rows={4} value={form.message} onChange={set("message")}
                  placeholder="Tell me about your goals…"
                  className={`${inputCls} border-cream/25 focus:border-gold resize-none`} data-testid="contact-message-input" />
              </div>
              <div className="sm:col-span-2 flex flex-wrap items-center gap-6">
                <button type="submit" disabled={status === "sending"}
                  className="btn-fill border border-cream px-10 py-4 text-sm tracking-wide disabled:opacity-60 inline-flex items-center gap-2"
                  data-testid="contact-submit-button">
                  {status === "sending" && <Loader2 size={16} className="animate-spin" />}
                  Send my enquiry
                </button>
                {status === "success" && (
                  <p className="text-gold text-sm" data-testid="contact-success-message">
                    Thank you! Cécile will reply very soon.
                  </p>
                )}
                {status === "error" && (
                  <p className="text-red-300 text-sm" data-testid="contact-error-message">
                    Sending failed — please try again.
                  </p>
                )}
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
